import { authorizedFetch } from "./transport";

export interface BoardEvent {
  id: string;
  sequence: number;
  event_type: string;
  ticket_id?: string | null;
  correlation_id?: string | null;
  metadata: Record<string, unknown>;
}

let active: AbortController | null = null;

function parseBlock(block: string): { id?: string; data?: string } {
  const result: { id?: string; data?: string } = {};
  for (const line of block.split("\n")) {
    if (line.startsWith("id:")) result.id = line.slice(3).trim();
    if (line.startsWith("data:")) result.data = (result.data || "") + line.slice(5).trim();
  }
  return result;
}

export function deliverBlock(
  boardId: string,
  block: string,
  onEvent: (event: BoardEvent) => void,
): string | null {
  const parsed = parseBlock(block);
  if (!parsed.data) return null;
  const event = JSON.parse(parsed.data) as BoardEvent;
  onEvent(event);
  // Advance the durable replay cursor only after the payload was valid and
  // the consumer accepted it. A parse/render failure must replay on reconnect.
  if (parsed.id) sessionStorage.setItem(`kanban.sse.${boardId}`, parsed.id);
  return parsed.id || null;
}

export function subscribe(
  boardId: string,
  onEvent: (event: BoardEvent) => void,
): () => void {
  active?.abort();
  const controller = new AbortController();
  active = controller;
  let lastEventId = sessionStorage.getItem(`kanban.sse.${boardId}`);

  const connect = async (): Promise<void> => {
    while (!controller.signal.aborted) {
      try {
        const headers = new Headers();
        if (lastEventId) headers.set("Last-Event-ID", lastEventId);
        const response = await authorizedFetch(`/api/v1/boards/${boardId}/events/stream`, {
          headers,
          signal: controller.signal,
        });
        if (!response.ok || !response.body) throw new Error("SSE connection failed");
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (!controller.signal.aborted) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
          let split = buffer.indexOf("\n\n");
          while (split >= 0) {
            const block = buffer.slice(0, split);
            buffer = buffer.slice(split + 2);
            const deliveredId = deliverBlock(boardId, block, onEvent);
            if (deliveredId) lastEventId = deliveredId;
            split = buffer.indexOf("\n\n");
          }
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
    }
  };
  void connect();
  return () => controller.abort();
}
